import type { FollowUpPlan } from '@/modules/followUpPlan';
import type { Invoice } from './billing.model';
import type { InvoiceBuilderTarget } from './billing.slice';
import { toCentimes } from './billingLine.utils';

/** The quote already issued for a builder target, if any. */
export const findTargetInvoice = (
  invoices: Invoice[] | undefined,
  target: InvoiceBuilderTarget
): Invoice | undefined =>
  invoices?.find((inv) =>
    target.kind === 'event'
      ? !inv.followUpPlanId
      : inv.followUpPlanId === target.planId
  );

/** The open plan a builder target aims at; undefined for the event target or a plan not in the list. */
export const findTargetPlan = (
  plans: FollowUpPlan[] | undefined,
  target: InvoiceBuilderTarget
): FollowUpPlan | undefined =>
  target.kind === 'plan'
    ? plans?.find((plan) => plan.id === target.planId)
    : undefined;

/**
 * Rounds a euro amount to the cent, counting cents the same way {@link allocateInvoiceTotalByBrss}
 * does. Every money figure in the devis builder goes through this so a total and its per-code
 * split can never disagree on a half cent (the kit `roundToPlaces` rounds differently).
 */
export const roundToCents = (amount: number) => toCentimes(amount) / 100;

/**
 * Splits an invoice total into cent-precise shares that always sum to the
 * rounded invoice total. Equal remainders keep the original procedure order.
 */
export const allocateInvoiceTotalByBrss = (
  total: number,
  brssValues: readonly number[]
): number[] => {
  const roundedTotalInCents = toCentimes(total);
  const nonNegativeBrssValues = brssValues.map((brss) => Math.max(0, brss));
  const brssSum = nonNegativeBrssValues.reduce((sum, brss) => sum + brss, 0);

  if (!brssSum) {
    return brssValues.map(() => 0);
  }

  const allocations = nonNegativeBrssValues.map((brss, index) => {
    const exactShareInCents = (roundedTotalInCents * brss) / brssSum;
    const allocatedShareInCents = Math.floor(exactShareInCents);
    return {
      index,
      allocatedShareInCents,
      fractionalRemainder: exactShareInCents - allocatedShareInCents
    };
  });
  const remainingUnits =
    roundedTotalInCents -
    allocations.reduce(
      (sum, allocation) => sum + allocation.allocatedShareInCents,
      0
    );

  allocations
    .sort(
      (first, second) =>
        second.fractionalRemainder - first.fractionalRemainder ||
        first.index - second.index
    )
    .slice(0, remainingUnits)
    .forEach((allocation) => {
      allocation.allocatedShareInCents += 1;
    });

  return allocations
    .sort((first, second) => first.index - second.index)
    .map((allocation) => allocation.allocatedShareInCents / 100);
};
